'use client';

import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useInstallmentCalculatorTracking } from '../../../components/Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { Slider } from './Slider/Slider.client';
import { SelectedInstallmentDisplay } from '../SelectedInstallmentDisplay/SelectedInstallmentDisplay';
import { SliderMessages } from '../messages';
import type { InstallmentSelectedHandler } from '../Slider.client.types';

const sliderSize = 212; // magic number to fit layout

export type CircleSliderProps = {
  /**
   * the viable installments array from the response
   * used to get min and max value for slider calculation
   */
  viableInstallments: number[];
  /**
   * the actual installments from the response
   */
  numberOfInstallments: number;
  /**
   * a handler function triggert on circle slider debounce
   */
  installmentSelectedHandler: InstallmentSelectedHandler;
};

/**
 * circle slider atom
 *
 * atom to display a circle slider
 *
 * */
export const CircleSlider = ({
  viableInstallments,
  numberOfInstallments,
  installmentSelectedHandler,
}: CircleSliderProps) => {
  const { formatMessage } = useIntl();
  const [sliderValue, setSliderValue] =
    useState<CircleSliderProps['numberOfInstallments']>(numberOfInstallments);
  const { trackSliderClick } = useInstallmentCalculatorTracking();

  useEffect(() => {
    setSliderValue(numberOfInstallments);
  }, [numberOfInstallments]);

  const onChangeHandler = (val: number) => {
    setSliderValue(val);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (numberOfInstallments !== sliderValue) {
      timer = setTimeout(() => {
        installmentSelectedHandler(sliderValue);
        trackSliderClick(sliderValue.toString());
      }, 500);
    }

    return () => clearTimeout(timer);
  }, [installmentSelectedHandler, sliderValue, numberOfInstallments, trackSliderClick]);

  return (
    <div className="relative mx-auto" style={{ width: `${sliderSize}px` }}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <SelectedInstallmentDisplay
          selectedInstallment={sliderValue}
          unit={formatMessage(SliderMessages.Month, {
            installment: sliderValue,
          })}
        />
      </div>
      <Slider
        value={sliderValue}
        min={viableInstallments[0]}
        max={viableInstallments[viableInstallments.length - 1]}
        size={sliderSize}
        onChange={onChangeHandler}
      />
    </div>
  );
};
